import {action, computed, makeObservable, observable, runInAction} from 'mobx';
import {RootStore} from './index';
import {BaseStore} from './base.store';
import customerService from '../../services/customer.service';
import {Customer, CustomerWithServices, ServiceDto, SidMetadata} from '@src/dtos/customer.dto';
import {IStore} from '../shared/common';
import {ATT_SOURCE} from '@src/utils/constants';
import {
  ActivityActionsRequest,
  CircuitType,
  LevelName,
  PolicyMode,
  SmartPolicyTaskStatus,
  VersionLabel
} from '@src/utils/enums';
import {EventType} from 'src/dtos/analytics.dto';
import moment from 'moment';
import {isVersionDraft, isVersionSmart} from '@src/utils/versionUtils';
import {SystemConfiguration} from '@src/utils/system-configuration';
import {isScheduledSource, isScheduledTarget, isScheduledVersionUpcoming} from '@src/utils/scheduling-utils';
import {
  ConnectionModel,
  isInitialPolicyModeUndefinedByService,
  parseConnection,
  parseConnections
} from '../features/ecm/connection.model';
import {ConnectionDataMap} from '@src/dtos/policy-bulk-update.dto';
import {SubscriptionFrequency, SubscriptionType} from '@src/dtos/user-subscription.dto';

export class CustomerStore extends BaseStore implements IStore<Customer>{

  public data: Customer[] | undefined = undefined;
  public selectedCustomer: CustomerWithServices | undefined = undefined;
  public selectedLocation: ServiceDto | undefined = undefined;
  public isLoading: boolean = false;
  public isLoadingServices: boolean = false;
  public isDuringCommit: boolean = false;
  public committedPolicyId: string | undefined = undefined;
  public committedPolicyStatus: string | undefined = undefined;
  public customerId: number = 0;
  public serviceId: number = 0;
  public isAvailable: boolean = true;
  public sidMetadata: SidMetadata | undefined = undefined;
  public isScheduledVersionUpcoming: boolean = false;
  public connectionList: ConnectionModel[] | undefined = undefined;
  public toastData: { title: string, desc: string } | null = null;
  public isEligibleForExecutiveReport: boolean | undefined = undefined;
  public frequencyOfExecutiveSummaryReport: SubscriptionFrequency | undefined = undefined;

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      data: observable,
      isLoading: observable,
      isLoadingServices: observable,
      isDuringCommit: observable,
      committedPolicyId: observable,
      committedPolicyStatus: observable,
      selectedLocation: observable,
      serviceName: computed,
      setLoading: action,
      load: action,
      setCustomer: action,
      setCustomerAndLocation: action,
      customerId: observable,
      serviceId: observable,
      filteredCustomers: computed,
      selectedCustomer: observable,
      isAvailable: observable,
      policyMode: computed,
      loadSelectedCustomer: action,
      getSmartPolicyOffsetFromReady: computed,
      isPolicyModeFirstTime: computed,
      isSmartPolicyDraftReady: computed,
      isSmartPolicySecondVersionDraftReady: computed,
      isQuickStartDraftReady: computed,
      customersConnectionsList: computed,
      isOneConnectionNonBronze: computed,
      sidMetadata: observable,
      isScheduledVersionUpcoming: observable,
      isScheduledVersionBarVisible: computed,
      isSourceScheduledVersion: computed,
      isTargetScheduledVersion: computed,
      setServiceNameAndPolicyMode: action,
      areAllServicesPolicyModeUndefined: computed,
      areAllOtherServicesPolicyModeUndefined: computed,
      isSingleConnection: computed,
      connectionList: observable,
      toastData: observable,
      setToastData: action,
      hasAtLeastOneNonInitialConnection: computed,
      setCustomerById: action,
      resetConnectionsData: action,
      hasBronzeDnsFeatures: computed,
      isAdvancedConnection: computed,
      isPremium: computed,
      hasNonBronzeConnection: computed,
      isEligibleForExecutiveReport: observable,
      getIsEligibleForExecutiveReport: action,
      frequencyOfExecutiveSummaryReport: observable,
      getFrequencyOfExecutiveSummaryReport: action,
      updateFrequencyOfExecutiveSummaryReport: action,
      isSmartProtectionRunning: computed,
      isSmartDraftCreationInProgress: computed
    });
  }

  get customersConnectionsList() {
    return Object.keys(this.rootStore.fwVersionStore.allFwVersions).map(_ => Number.parseInt(_));
  }

  public async load() {
    try {
      this.isLoading = true;
      const res = await customerService.getAllCustomers();
      runInAction(() => {
        this.data = res;
        this.isLoading = false;
      });
    }
    catch (e) {
      this.isLoading = false;
      throw e;
    }
  }

  public async loadSelectedCustomer(customerId: number) {
    try {
      this.isLoadingServices = true;
      const res = await customerService.getAllCustomerServices(customerId);
      runInAction(() => {
        const customer = this.data!.find(c => c.customerId === customerId)!;
        this.selectedCustomer = { ...customer, services: res};
        this.isLoadingServices = false;
      });
    }
    catch (e) {
      this.isLoadingServices = false;
      throw e;
    }
  }

  public async setCustomer(customer: Customer) {
     await runInAction(async () => {
      if (this.selectedCustomer) {
        this.rootStore.reset();
      }
      await this.loadSelectedCustomer(customer.customerId);
    });
  }

  reset() {
    super.reset();
    this.selectedLocation = undefined;
    this.selectedCustomer = undefined;
    this.data = undefined;
    this.isEligibleForExecutiveReport = undefined;
    this.frequencyOfExecutiveSummaryReport = undefined;
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setToastData(toastData: { title: string, desc: string } | null) {
    this.toastData = toastData;
  }

  public setSelectedLocation(service: ServiceDto) {
    runInAction(() => {
      this.selectedLocation = service;
      this.serviceId = service.serviceId;
    });
  }

  get serviceName() {
    return this.selectedLocation?.serviceName || this.selectedLocation?.adiCircuitId;
  }

  public async setCustomerById(customerId: number) {
    this.customerId = customerId;
    const customer = this.data?.find(c => c.customerId === customerId);
    if (customer) {
      await this.setCustomer(customer);
    }
  }

  public async setCustomerAndLocation(customerId: number, serviceId: number) {
    if (this.customerId !== customerId || this.serviceId !== serviceId || !this.selectedCustomer || !this.selectedLocation) {
      this.customerId = customerId;
      this.serviceId = serviceId;
      const customer = this.data?.find(c => c.customerId === customerId);
      if (customer) {
        await this.setCustomer(customer);
        runInAction(() => {
          this.selectedLocation = this.selectedCustomer?.services.find(service => service.serviceId === serviceId);
          this.handleSignInAndOutEvents();
        });
      }
    }
  }

  get filteredCustomers() {
    if (this.rootStore.userStore.data?.source === ATT_SOURCE) {
      return this.data;
    }
    const userAllowedCustomers = this.rootStore.userStore.data?.allowedCustomers?.map(ac => ac.customerId);
    return this.data?.filter(customer => userAllowedCustomers?.includes(customer.customerId));
  }

  public async getServiceStatus() {
      const res = await customerService.getServiceAvailability(this.customerId, this.serviceId);
      runInAction(() => {
        this.isAvailable = res;
      });
  }

  public async getServiceInfo() {
    const res = await customerService.getServiceInfo(this.customerId, this.serviceId);
    runInAction(() => {
      this.sidMetadata = res;
      this.isScheduledVersionUpcoming = false;
      if (this.sidMetadata?.scheduledDate) {
        this.isScheduledVersionUpcoming = isScheduledVersionUpcoming(this.sidMetadata.scheduledDate);
      }
      if (res && this.selectedLocation) {
        this.selectedLocation.smartPolicyTaskStatus = res.smartPolicyTaskStatus; // update smart policy task status in customer store based on service info response
      }
    });
  }

  get isScheduledVersionBarVisible() {
    if (this.sidMetadata?.scheduleType && this.sidMetadata.scheduledDate) {
      return this.isSourceScheduledVersion ? this.isScheduledVersionUpcoming : true;
    }
    return false;
  }

  get isSourceScheduledVersion() {
    return isScheduledSource(this.sidMetadata?.scheduleType);
  }

  get isTargetScheduledVersion() {
    return isScheduledTarget(this.sidMetadata?.scheduleType);
  }

  private handleSignInAndOutEvents() {
    const oldCustomerId = Number(sessionStorage.getItem('cid'));
    const oldSid = Number(sessionStorage.getItem('sid'));
    const customerId = Number(this.selectedCustomer?.customerId.toString());
    const sid = Number(this.selectedLocation?.serviceId.toString());

    if (!customerId || !sid) return;

    sessionStorage.setItem('sid', sid.toString());
    sessionStorage.setItem('cid', customerId.toString());

    if (oldCustomerId && oldSid && sid !== oldSid) {
      void this.rootStore.activityStore.sendActivityLog(ActivityActionsRequest.SIGN_OUT, oldCustomerId, oldSid);
      this.sendEvent(EventType.CustomerSignOut, { oldCustomerId, oldSid });
    }
    if (!oldSid || sid !== oldSid) {
      void this.rootStore.activityStore.sendActivityLog(ActivityActionsRequest.SIGN_IN);
      this.sendEvent(EventType.CustomerSignIn);
    }
  }

  get policyMode() {
    const fromList = this.connectionList?.find(c => c.id === this.serviceId);
    return fromList?.policyMode ?? this.selectedLocation?.policyMode;
  }

  get isPolicyModeFirstTime() {
    if (this.policyMode === PolicyMode.SMART_POLICY) {
      return this.rootStore.fwVersionStore.isOneVersionActive;
    } else {
      return this.rootStore.fwVersionStore.isInitialVersion;
    }
  }

  get isSmartPolicyDraftReady() {
    return isVersionDraft(this.rootStore.fwVersionStore.fwVersions?.[0]) &&
        isVersionSmart(this.rootStore.fwVersionStore.fwVersions?.[0]);
  }

  get isSmartPolicySecondVersionDraftReady() {
     return this.rootStore.fwVersionStore.fwVersions?.length === 2  && 
        isVersionDraft(this.rootStore.fwVersionStore.fwVersions?.[0]) &&
        isVersionSmart(this.rootStore.fwVersionStore.fwVersions?.[0]);
  }

  get isQuickStartDraftReady() {
    return this.policyMode === PolicyMode.QUICK_START &&
        this.rootStore.fwVersionStore.fwVersions?.length === 2 &&
        isVersionDraft(this.rootStore.fwVersionStore.fwVersions[0]);
  }

  private updateSelectedCustomerAndLocation(updatedData: ConnectionDataMap) {
    runInAction(() => {
      const keys = Object.keys(updatedData);
      const selectedLocationId = keys.length === 1 ? Number(keys[0]) : this.selectedLocation?.serviceId;

      if (selectedLocationId && updatedData[selectedLocationId]) {
        this.setSelectedLocation(updatedData[selectedLocationId]);
      }

      if (this.selectedCustomer?.services) {
        this.selectedCustomer.services = this.selectedCustomer.services.map(
            service => updatedData[service.serviceId] ?? service
        );
      }

      this.connectionList = this.connectionList?.map(
          conn => updatedData[conn.id] ? parseConnection(updatedData[conn.id], this.customerId) : conn
        );
    });
  }

  public async setQuickPolicy(destinationServiceIdList: number[], toApply: boolean) {
    const res = await customerService.bulkQuickPolicy(this.customerId, { destinationServiceIdList, toApply });
    this.updateSelectedCustomerAndLocation(res);
  }

  public async setCustomPolicy(destinationServiceIdList: number[]) {
    const res = await customerService.bulkCustomPolicy(this.customerId, { destinationServiceIdList });
    this.updateSelectedCustomerAndLocation(res);
  }

  public async setSmartPolicy(destinationServiceIdList: number[]) {
    const res = await customerService.bulkSmartPolicy(this.customerId, { destinationServiceIdList });
    this.updateSelectedCustomerAndLocation(res);
  }

  public async bulkPullPolicy(serviceId:number, destinationServiceIdList: number[], toApply: boolean) {
    const res = await customerService.bulkPullPolicy(this.customerId, serviceId,
        { destinationServiceIdList, toApply });
    this.updateSelectedCustomerAndLocation(res);
  }

  public async applyBulkDrafts(destinationServiceIdList: number[]) {
    const res = await customerService.bulkDrafts(this.customerId, { destinationServiceIdList });
    this.updateSelectedCustomerAndLocation(res);
  }

  public async setServiceNameAndPolicyMode(customerId: number, serviceId: number, updatedServiceName: string, policyMode: PolicyMode) {
    if (policyMode === PolicyMode.SMART_POLICY) {
      this.rootStore.uiStore.setSmartPolicyModalShownByServiceId(undefined);
    }
    const res = await customerService.setServiceNameAndPolicyMode(customerId, serviceId, updatedServiceName, policyMode);
    this.updateSelectedCustomerAndLocation(res);
  }

  public async rollbackPolicyMode(destinationServiceIdList: number[]) {
    const res = await customerService.rollbackPolicyMode(this.customerId, destinationServiceIdList);
    this.updateSelectedCustomerAndLocation(res);
  }

  get isSmartProtectionRunning(): boolean {
    return this.selectedLocation?.smartPolicyTaskStatus === SmartPolicyTaskStatus.PENDING ||
        this.selectedLocation?.smartPolicyTaskStatus === SmartPolicyTaskStatus.IN_PROGRESS;
  }

  get isSmartDraftCreationInProgress(): boolean {
    return this.selectedLocation?.smartPolicyTaskStatus === SmartPolicyTaskStatus.IN_PROGRESS;
  }

  public async setConnectionName(connectionId: number, updatedConnectionName?: string) {
    const res = await customerService.updateConnectionName(this.customerId, connectionId, updatedConnectionName);
    this.updateSelectedCustomerAndLocation(res);
  }

  public async setConnectionDetails(connectionId: number, externalId: string,
                                    connectionName?: string, policyName?: string, policyComments?: string) {
    const res = await customerService.updateConnectionDetails(
        this.customerId, connectionId, externalId, connectionName, policyName, policyComments);
    this.updateSelectedCustomerAndLocation(res);
  }

  get getSmartPolicyOffsetFromReady() {
    const dateInMoment = moment.utc(this.rootStore.fwVersionStore.getActiveVersion?.updatedTimestamp)
        .add(SystemConfiguration.getInstance().policyGeneratorAnalysisDays, 'days');
    const now = moment();
    return now.isAfter(dateInMoment) ? 0 : dateInMoment.diff(now);
  }

  public getCustomerServiceData = (sid: number): ServiceDto | undefined => {
    return this.selectedCustomer?.services.find(service => service.serviceId === sid);
  };

  get isOneConnectionNonBronze() {
    return this.selectedCustomer?.services?.filter(_ => _.serviceLevel !== LevelName.BRONZE)?.length === 1;
  }

  public loadConnectionsData = async (customerId: number) => {
    const res = await customerService.getConnectionData(customerId);
    runInAction(() => {
      this.connectionList = parseConnections(customerId, res);
    });
  };

  public resetConnectionsData = () => {
    this.connectionList = undefined;
  };

  get areAllServicesPolicyModeUndefined() {
    return this.selectedCustomer?.services.every(service => isInitialPolicyModeUndefinedByService(service)) ?? false;
  }

  get areAllOtherServicesPolicyModeUndefined() {
    return this.selectedCustomer?.services.every(
        service => isInitialPolicyModeUndefinedByService(service) || service.serviceId === this.selectedLocation?.serviceId
    );
  }

  get isSingleConnection() {
    return this.selectedCustomer?.services?.length === 1;
  }

  get hasAtLeastOneNonInitialConnection() {
    return this.connectionList?.some(conn => conn.serviceLevel !== LevelName.BRONZE && conn.policyStatus !== VersionLabel.Initial) || false;
  }

  get isAdvancedConnection() {
    return this.selectedLocation?.serviceLevel === LevelName.SILVER;
  }

  get hasBronzeDnsFeatures() {
    const ct = this.selectedLocation?.circuitType;
    return ct === CircuitType.ABF || ct === CircuitType.AIA_B || ct === CircuitType.MOBILE_CRU;
  }

  get isPremium() {
    return this.selectedLocation?.serviceLevel === LevelName.GOLD;
  }

  get hasNonBronzeConnection() {
    return this.selectedCustomer?.services?.some(service => service.serviceLevel !== LevelName.BRONZE) ?? false;
  }

  public async getIsEligibleForExecutiveReport() {
    if (this.isEligibleForExecutiveReport === undefined) {
      const res = await customerService.getCustomerEligibleForExecutiveSummary(this.customerId);
      runInAction(() => {
        this.isEligibleForExecutiveReport = res;
      });
    }
    return this.isEligibleForExecutiveReport;
  }

  public async getFrequencyOfExecutiveSummaryReport() {
    if (this.frequencyOfExecutiveSummaryReport === undefined) {
      const res = await customerService.getReportSubscription(this.customerId, SubscriptionType.REPORT_SUBSCRIPTION);
      runInAction(() => {
        this.frequencyOfExecutiveSummaryReport = res.frequency;
      });
    }
    return this.frequencyOfExecutiveSummaryReport;
  }

  public async updateFrequencyOfExecutiveSummaryReport(selectedFrequency: SubscriptionFrequency) {
    const res = await customerService.updateReportSubscription(this.customerId,
        {
          subscriptionType: SubscriptionType.REPORT_SUBSCRIPTION,
          frequency: selectedFrequency
        });
    runInAction(() => {
      this.frequencyOfExecutiveSummaryReport = res.frequency;
    });
    return this.frequencyOfExecutiveSummaryReport;
  }

}